import { ExpectedError } from "../../expectedError";
import { bearerAuth, oauthAuth } from "../standardAuthPatterns";
import logo from "./logo.svg";
import { defineProduct, productVariant } from "../types";

const KEYWORDS = "chat messaging im channels dms huddles workspace";

export const slack = defineProduct({
  name: "Slack",
  tagLine: "Team communication",
  logo,
  tags: ["featured", "communication"],
  variants: [
    productVariant({
      identifier: "slack",
      authLabel: "Bot token",
      keywords: KEYWORDS,
      protocol: "http",
      defaultUrlPattern: () => "https://slack\\.com/api/.*",
      auth: (app) => bearerAuth(app.secret.oauthToken),
      steps: [
        {
          title: "Create a custom app in Slack",
          items: [
            {
              kind: "markdown",
              content:
                "Visit [https://api.slack.com/apps](https://api.slack.com/apps) and select the app you would like to use, or create a new app.\n\nAdd the relevant permissions and scopes you need, then install it to your workspace (which may require admin approval).",
            },
          ],
        },
        {
          title: "Copy and paste the app’s OAuth token here",
          items: [
            {
              kind: "markdown",
              content:
                "Locate your token on the **OAuth & Permissions** tab when viewing your Slack app.\n\nPaste either the user token (if you enabled user scopes) or the bot user token (if you enabled bot scopes).",
            },
            {
              kind: "input",
              label: "OAuth token",
              placeholder: "xoxb-...",
              required: true,
              target: { kind: "secrets", field: "oauthToken" },
            },
          ],
        },
      ],
      connectionTest: {
        protocol: "http",
        request: () => ({
          url: "https://slack.com/api/auth.test",
          expectedBodyContains: '"ok":true',
        }),
      },
    }),
    productVariant({
      identifier: "slackUser",
      authLabel: "User OAuth",
      keywords: KEYWORDS,
      protocol: "http",
      defaultUrlPattern: () => "https://slack\\.com/api/.*",
      auth: oauthAuth,
      steps: [
        {
          title: "Connect your Slack account",
          items: [
            {
              kind: "oauth",
            },
          ],
        },
      ],
      oauth: {
        grantType: () => "authorization_code",
        authorizationEndpoint: () =>
          "https://slack.com/oauth/v2_user/authorize",
        tokenEndpoint: () => "https://slack.com/api/oauth.v2.user.access",
        tokenEndpointAuthMethod: () => "client_secret_post",
        clientCredentialsFrom: "env",
        // Slack's oauth.v2.user.access returns the user token with a
        // non-standard token_type of "user" (not "bearer") and signals
        // failure as HTTP 200 with {"ok": false}.
        normalizeTokenResponse: (body) => {
          if (body.ok === false) {
            throw new ExpectedError(
              `Slack OAuth error: ${typeof body.error === "string" ? body.error : "unknown"}`
            );
          }
          return { ...body, token_type: "bearer" };
        },
      },
      connectionTest: {
        protocol: "http",
        request: () => ({
          url: "https://slack.com/api/auth.test",
          expectedBodyContains: '"ok":true',
        }),
      },
    }),
  ],
});
